import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, CheckCircle, Info, UserMinus, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useThemeStore } from '../../lib/themeStore';
import { useEmbedStore } from '../../lib/embedStore';
import { isTslhTenant } from '../../lib/tenantStore';
import { clearLeagueRulesAcceptance } from '../../lib/leagueRulesAcceptance';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import {
  getFreeAgentsStandard,
  getFreeAgentsStandardGoalie,
  getStandardPlayerSelectionStats,
  signPlayerStandard,
  completeInitialBuild,
  releasePlayer,
} from '../../lib/supabase';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';
import { isOnboardingDisabled } from '../../lib/onboarding';
import { supabase } from '../../lib/supabase';
import { FreeAgentsTable } from '../FreeAgents/FreeAgentsTable';
import { FreeAgentsGoalieTable } from '../FreeAgents/FreeAgentsGoalieTable';
import { FreeAgent, FreeAgentGoalie, SortConfig, SortField } from '../FreeAgents/types';
import '../FreeAgents/FreeAgentsEmbed.css';
import './BuildMyTeamEmbed.css';

const ARSENAL_FONTS_ID = 'dp-arsenal-embed-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

const NHL_TEAMS = [
  'ANA', 'UTA', 'BOS', 'BUF', 'CAR', 'CBJ', 'CGY', 'CHI', 'COL', 'DAL',
  'DET', 'EDM', 'FLA', 'LAK', 'MIN', 'MTL', 'NSH', 'NJD', 'NYI', 'NYR',
  'OTT', 'PHI', 'PIT', 'SEA', 'SJS', 'STL', 'TBL', 'TOR', 'VAN', 'VGK',
  'WSH', 'WPG',
];
const POSITION_CHECKBOXES = ['ATT', 'DEF', 'G'] as const;
type PositionGroup = (typeof POSITION_CHECKBOXES)[number];
const ITEMS_PER_PAGE = 50;
const UNLIMITED_SALARY_FILTER = '';

const parseSalaryMillions = (value: string): number | undefined => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return undefined;

  const millions = Number(normalized);
  if (!Number.isFinite(millions) || millions < 0) return undefined;

  return millions * 1_000_000;
};

const formatSalaryMillionsInput = (salary: number): string => {
  const millions = salary / 1_000_000;
  return millions.toFixed(1).replace(/\.0$/, '');
};

interface SelectionStats {
  selected_completed_teams?: number;
  completed_teams_total?: number;
  selection_percentage?: number;
}

const buildSelectionStatsMap = (rows: SelectionStats[] | null | undefined) => {
  const statsByPlayerId = new Map<number, SelectionStats>();
  (rows ?? []).forEach((row: any) => {
    statsByPlayerId.set(Number(row.player_id), {
      selected_completed_teams: Number(row.selected_completed_teams ?? 0),
      completed_teams_total: Number(row.completed_teams_total ?? 0),
      selection_percentage: Number(row.selection_percentage ?? 0),
    });
  });
  return statsByPlayerId;
};

const withSelectionStats = <T extends { playerid: number }>(players: T[], statsByPlayerId: Map<number, SelectionStats>): T[] =>
  players.map((player) => ({
    ...player,
    selected_completed_teams: statsByPlayerId.get(Number(player.playerid))?.selected_completed_teams ?? 0,
    completed_teams_total: statsByPlayerId.get(Number(player.playerid))?.completed_teams_total ?? 0,
    selection_percentage: statsByPlayerId.get(Number(player.playerid))?.selection_percentage ?? 0,
  }));

interface RosterCounts {
  forwards: number;
  defense: number;
  goalies: number;
  bench: number;
}

interface LeagueSettings {
  number_foward: number;
  number_def: number;
  number_goalie: number;
  number_reserve: number;
  salary_cap: boolean;
  max_salary_cap: number;
  salary_cap_reserve: boolean;
  max_salary_cap_reserve: number;
  // point system fields (from src/lib/api/leagueSettings.ts)
  pts_goal_att?: number;
  pts_ass_att?: number;
  pts_goal_def?: number;
  pts_ass_def?: number;
  pts_goal_g?: number;
  pts_win_g?: number;
  pts_shu_g?: number;
  pts_otlosse_g?: number;
  // limits
  max_signing?: number;
  max_injury?: number;
  count_free_agent_signings?: boolean;
}

type ActiveTab = 'skaters' | 'goalies';

interface DraftedPlayer {
  playerid: number;
  name: string;
  positioncode: string; // 'C' | 'L' | 'R' | 'D' | 'G'
  isGoalie: boolean;
  teamabbrevs: string;
  salary: number;
  isnhl: boolean; // true = équipe principale, false = réserve/banc
}

export function BuildMyTeam() {
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { theme } = useThemeStore();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  useArsenalFonts(isEmbed);
  const { isStandardFlexible, isBuildPhase, isLoading: modeLoading } = useLeagueMode();

  const [skaters, setSkaters] = useState<FreeAgent[]>([]);
  const [goalies, setGoalies] = useState<FreeAgentGoalie[]>([]);
  const [rosterCounts, setRosterCounts] = useState<RosterCounts>({ forwards: 0, defense: 0, goalies: 0, bench: 0 });
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingInProgress, setSigningInProgress] = useState<Set<number>>(new Set());
  const [completing, setCompleting] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('skaters');
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [currentSalaryCap, setCurrentSalaryCap] = useState<number>(0);
  const [currentReserveSalaryCap, setCurrentReserveSalaryCap] = useState<number>(0);
  const [highlightedPos, setHighlightedPos] = useState<Set<string>>(new Set());

  // Section "Joueurs repêchés" (signés lors de la phase de construction)
  const [draftedPlayers, setDraftedPlayers] = useState<DraftedPlayer[]>([]);
  const [showDraftedSection, setShowDraftedSection] = useState(false);
  const [releasingIds, setReleasingIds] = useState<Set<number>>(new Set());
  const [pendingReleasePlayer, setPendingReleasePlayer] = useState<DraftedPlayer | null>(null);

  // Modal récapitulatif ligue visible à l'entrée de la page (phase build)
  const [showLeagueSummaryModal, setShowLeagueSummaryModal] = useState(false);
  const leagueModalRef = useRef<HTMLDivElement | null>(null);

  // Modal de choix de destination (principale / réserve)
  const [pendingSignPlayer, setPendingSignPlayer] = useState<FreeAgent | FreeAgentGoalie | null>(null);
  const signModalRef = useRef<HTMLDivElement | null>(null);
  // Preserve previous scroll position to restore after closing the sign modal
  const previousScrollRef = useRef<number | null>(null);
  const releaseModalRef = useRef<HTMLDivElement | null>(null);
  // Résultat de la tentative de signature affiché dans le modal
  const [signResult, setSignResult] = useState<{ status: 'success' | 'error' | null; message?: string }>({ status: null });
  const [signModalBusy, setSignModalBusy] = useState(false);
  const signBusyRef = useRef(false);
  // Optional top padding for the sign modal overlay (used to display modal near grid start)
  const [signModalPaddingTop, setSignModalPaddingTop] = useState<number | null>(null);
  const [signModalAnchor, setSignModalAnchor] = useState<{ top: number; documentHeight: number; maxHeight: number } | null>(null);
  const signTriggerRef = useRef<HTMLElement | null>(null);
  const [releaseModalAnchor, setReleaseModalAnchor] = useState<{ top: number; documentHeight: number; maxHeight: number } | null>(null);
  const releaseTriggerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!pendingSignPlayer || !signModalAnchor) return;
    const modal = signModalRef.current;
    if (!modal) return;
    const positionModal = () => {
      const height = modal.getBoundingClientRect().height;
      modal.style.top = `${Math.max(12, Math.min(
        signModalAnchor.top - height / 2,
        signModalAnchor.documentHeight - height - 12,
      ))}px`;
    };
    positionModal();
    modal.focus({ preventScroll: true });
    const observer = new ResizeObserver(positionModal);
    observer.observe(modal);
    return () => observer.disconnect();
  }, [pendingSignPlayer, signModalAnchor]);

  useLayoutEffect(() => {
    if (!pendingReleasePlayer || !releaseModalAnchor) return;
    const modal = releaseModalRef.current;
    if (!modal) return;
    const positionModal = () => {
      const height = modal.getBoundingClientRect().height;
      modal.style.top = `${Math.max(12, Math.min(
        releaseModalAnchor.top - height / 2,
        releaseModalAnchor.documentHeight - height - 12,
      ))}px`;
    };
    positionModal();
    modal.focus({ preventScroll: true });
    const observer = new ResizeObserver(positionModal);
    observer.observe(modal);
    return () => observer.disconnect();
  }, [pendingReleasePlayer, releaseModalAnchor]);

  const handleRelease = (player: DraftedPlayer, event: React.MouseEvent<HTMLButtonElement>) => {
    const trigger = event.currentTarget;
    releaseTriggerRef.current = trigger;
    if (isEmbed && /iPhone/.test(navigator.userAgent)) {
      const rect = trigger.getBoundingClientRect();
      const documentHeight = document.documentElement.scrollHeight;
      setReleaseModalAnchor({
        top: window.scrollY + (event.detail > 0 ? event.clientY : rect.top + rect.height / 2),
        documentHeight,
        maxHeight: Math.max(120, Math.min(480, window.screen.height * 0.55, window.innerHeight - 24, documentHeight - 24)),
      });
    } else {
      setReleaseModalAnchor(null);
    }
    setPendingReleasePlayer(player);
  };

  const closeReleaseModal = useCallback((restoreToPage = false) => {
    setPendingReleasePlayer(null);
    setReleaseModalAnchor(null);
    if (releaseModalAnchor) {
      const focusTarget = !restoreToPage && releaseTriggerRef.current?.isConnected
        ? releaseTriggerRef.current
        : document.getElementById('freeagents-page');
      focusTarget?.focus({ preventScroll: true });
    }
    releaseTriggerRef.current = null;
  }, [releaseModalAnchor]);

  useEffect(() => {
    if (!pendingReleasePlayer || !releaseModalAnchor) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeReleaseModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingReleasePlayer, releaseModalAnchor, closeReleaseModal]);

  // Ref pour scroller vers la section "Joueurs repêchés"
  const draftedSectionRef = useRef<HTMLDivElement | null>(null);
  // Ref pour scroller vers la section de finalisation
  const completeClubSectionRef = useRef<HTMLDivElement | null>(null);

  // Filtres – même style que la page Agents Libres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPositions, setSelectedPositions] = useState<PositionGroup[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('ALL');
  const [minSalary, setMinSalary] = useState('');
  const [maxSalary, setMaxSalary] = useState(UNLIMITED_SALARY_FILTER);
  const [useRemainingCapAsMax, setUseRemainingCapAsMax] = useState(false);
  const [showCapInfo, setShowCapInfo] = useState(false);
  const [showPointSystem, setShowPointSystem] = useState(false);

  // Tri
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'points', direction: 'desc' });
  const [goaliesSortConfig, setGoaliesSortConfig] = useState<{ field: keyof FreeAgentGoalie; direction: 'asc' | 'desc' }>({
    field: 'wins',
    direction: 'desc',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const leagueId = selectedLeague?.id;
  const userId = currentUserId ? Number(currentUserId) : null;

  const handleViewPlayerDetails = (player: { playerid: number }) => {
    navigate(`/players/${player.playerid}`);
  };

  // Construit la liste des joueurs repêchés à partir des résultats bruts des vues roster
  const buildDraftedPlayersList = useCallback((
    mainPlayers: any[],
    reservePlayers: any[],
    mainGoalies: any[],
    reserveGoalies: any[],
  ): DraftedPlayer[] => {
    const drafted: DraftedPlayer[] = [];
    mainPlayers.forEach((row) => drafted.push({
      playerid: row.playerid, name: row.skaterfullname ?? 'Joueur', positioncode: row.positioncode ?? '',
      isGoalie: false, teamabbrevs: row.teamabbrevs ?? '', salary: row.salary ?? 0, isnhl: true,
    }));
    reservePlayers.forEach((row) => drafted.push({
      playerid: row.playerid, name: row.skaterfullname ?? 'Joueur', positioncode: row.positioncode ?? '',
      isGoalie: false, teamabbrevs: row.teamabbrevs ?? '', salary: row.salary ?? 0, isnhl: false,
    }));
    mainGoalies.forEach((row) => drafted.push({
      playerid: row.playerid, name: row.goaliefullname ?? 'Gardien', positioncode: 'G',
      isGoalie: true, teamabbrevs: row.teamabbrevs ?? '', salary: row.salary ?? 0, isnhl: true,
    }));
    reserveGoalies.forEach((row) => drafted.push({
      playerid: row.playerid, name: row.goaliefullname ?? 'Gardien', positioncode: 'G',
      isGoalie: true, teamabbrevs: row.teamabbrevs ?? '', salary: row.salary ?? 0, isnhl: false,
    }));
    return drafted;
  }, []);

  const fetchData = useCallback(async () => {
    if (!leagueId || !userId) return;
    setLoading(true);

    try {
      // Fetch league settings
      const { data: settings } = await getLeagueSettings(leagueId);
      setLeagueSettings(settings);

      // Fetch free agents (non-exclusive: only excludes MY players)
      const [skatersResult, goaliesResult, selectionStatsResult] = await Promise.all([
        getFreeAgentsStandard(leagueId, userId),
        getFreeAgentsStandardGoalie(leagueId, userId),
        getStandardPlayerSelectionStats(leagueId),
      ]);

      const selectionStatsByPlayerId = buildSelectionStatsMap(selectionStatsResult.data);
      if (skatersResult.data) setSkaters(withSelectionStats(skatersResult.data as FreeAgent[], selectionStatsByPlayerId));
      if (goaliesResult.data) setGoalies(withSelectionStats(goaliesResult.data as FreeAgentGoalie[], selectionStatsByPlayerId));

      // Fetch salary caps
      const { data: mainCapData, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', true)
        .single();
      if (!mainCapError || mainCapError.code === 'PGRST116') {
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);
      }

      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', false)
        .single();
      if (!reserveCapError || reserveCapError.code === 'PGRST116') {
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      }

      // Fetch current roster counts via getmyteamplayer (skaters) + getmyteamgoalie (goalies)
      const [mainPlayersResult, reservePlayersResult, mainGoaliesResult, reserveGoaliesResult] = await Promise.all([
        supabase
          .from('getmyteamplayer')
          .select('playerid, skaterfullname, positioncode, teamabbrevs, salary')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', true),
        supabase
          .from('getmyteamplayer')
          .select('playerid, skaterfullname, positioncode, teamabbrevs, salary')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', false),
        supabase
          .from('getmyteamgoalie')
          .select('playerid, goaliefullname, teamabbrevs, salary')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', true),
        supabase
          .from('getmyteamgoalie')
          .select('playerid, goaliefullname, teamabbrevs, salary')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', false),
      ]);

      const counts = { forwards: 0, defense: 0, goalies: 0, bench: 0 };
      (mainPlayersResult.data ?? []).forEach((row: any) => {
        const pos = row.positioncode ?? '';
        if (pos === 'D') counts.defense++;
        else counts.forwards++;
      });
      counts.goalies = mainGoaliesResult.data?.length ?? 0;
      counts.bench = (reservePlayersResult.data?.length ?? 0) + (reserveGoaliesResult.data?.length ?? 0);
      setRosterCounts(counts);

      setDraftedPlayers(buildDraftedPlayersList(
        mainPlayersResult.data ?? [],
        reservePlayersResult.data ?? [],
        mainGoaliesResult.data ?? [],
        reserveGoaliesResult.data ?? [],
      ));
    } catch (err) {
      console.error('[BuildMyTeam] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId, userId, isBuildPhase, buildDraftedPlayersList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Show league summary modal once when entering the page in build phase
  useEffect(() => {
    if (!leagueId || !userId) return;
    // do not show until we have settings loaded
    if (loading) return;
    if (!leagueSettings) return;
    if (!isBuildPhase) return;

    // Superseded by the dedicated /league-rules-onboarding page (embed BPM flow).
    if (isOnboardingDisabled()) return;

    const key = `build-my-team-modal-dismissed:${leagueId}:${userId}`;
    const dismissed = localStorage.getItem(key);
    if (!dismissed) setShowLeagueSummaryModal(true);
  }, [leagueId, userId, loading, leagueSettings, isBuildPhase]);

  // Scroll to modal when it becomes visible
  useEffect(() => {
    if (!showLeagueSummaryModal) return;
    // Give the browser a tick to render modal then scroll to it
    setTimeout(() => {
      if (leagueModalRef.current) {
        leagueModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        leagueModalRef.current.focus?.();
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  }, [showLeagueSummaryModal]);

  // Silent refresh – counts + caps only, no loading spinner
  const refreshMetrics = useCallback(async () => {
    if (!leagueId || !userId) return;
    try {
      const [mainCapResult, reserveCapResult, mainPlayersResult, reservePlayersResult, mainGoaliesResult, reserveGoaliesResult] = await Promise.all([
        supabase.from('get_salary_cap_per_team').select('salary_cap').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', true).single(),
        supabase.from('get_salary_cap_per_team').select('salary_cap').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', false).single(),
        supabase.from('getmyteamplayer').select('playerid, skaterfullname, positioncode, teamabbrevs, salary').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', true),
        supabase.from('getmyteamplayer').select('playerid, skaterfullname, positioncode, teamabbrevs, salary').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', false),
        supabase.from('getmyteamgoalie').select('playerid, goaliefullname, teamabbrevs, salary').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', true),
        supabase.from('getmyteamgoalie').select('playerid, goaliefullname, teamabbrevs, salary').eq('league_id', leagueId).eq('user_id', userId).eq('isnhl', false),
      ]);
      if (!mainCapResult.error || mainCapResult.error.code === 'PGRST116')
        setCurrentSalaryCap(mainCapResult.data?.salary_cap || 0);
      if (!reserveCapResult.error || reserveCapResult.error.code === 'PGRST116')
        setCurrentReserveSalaryCap(reserveCapResult.data?.salary_cap || 0);
      const counts = { forwards: 0, defense: 0, goalies: 0, bench: 0 };
      (mainPlayersResult.data ?? []).forEach((row: any) => {
        const pos = row.positioncode ?? '';
        if (pos === 'D') counts.defense++;
        else counts.forwards++;
      });
      counts.goalies = mainGoaliesResult.data?.length ?? 0;
      counts.bench = (reservePlayersResult.data?.length ?? 0) + (reserveGoaliesResult.data?.length ?? 0);
      setRosterCounts(counts);

      setDraftedPlayers(buildDraftedPlayersList(
        mainPlayersResult.data ?? [],
        reservePlayersResult.data ?? [],
        mainGoaliesResult.data ?? [],
        reserveGoaliesResult.data ?? [],
      ));
    } catch (err) {
      console.error('[BuildMyTeam] refreshMetrics error:', err);
    }
  }, [leagueId, userId, buildDraftedPlayersList]);

  // Limites de l'alignement (disponibles dans handleSign et dans le rendu)
  const maxForwards = leagueSettings?.number_foward ?? 12;
  const maxDef = leagueSettings?.number_def ?? 6;
  const maxGoalies = leagueSettings?.number_goalie ?? 2;
  const maxBench = leagueSettings?.number_reserve ?? 5;

  // Ouvre le modal de choix de destination
  const handleSign = (player: FreeAgent | FreeAgentGoalie, event?: React.MouseEvent) => {
    const isIOSEmbed = isEmbed && typeof navigator !== 'undefined' && (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
    );
    const trigger = event?.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null;
    signTriggerRef.current = trigger;
    if (isIOSEmbed && trigger) {
      const rect = trigger.getBoundingClientRect();
      const documentHeight = document.documentElement.scrollHeight;
      setSignModalAnchor({
        top: window.scrollY + (event && event.detail > 0 ? event.clientY : rect.top + rect.height / 2),
        documentHeight,
        maxHeight: Math.max(120, Math.min(480, window.screen.height * 0.55, window.innerHeight - 24, documentHeight - 24)),
      });
    } else {
      setSignModalAnchor(null);
    }
    // Save current document scroll position so we can restore it when modal closes
    if (typeof window !== 'undefined') {
      previousScrollRef.current = window.scrollY || document.documentElement.scrollTop || 0;
    } else {
      previousScrollRef.current = null;
    }
    setSignModalBusy(false);
    signBusyRef.current = false;

    // If embed mode, keep existing behaviour (portal content handled separately)
    if (isEmbed || typeof window === 'undefined') {
      setSignModalPaddingTop(null);
      setPendingSignPlayer(player);
      return;
    }

    // Scroll the page to the start of the grid so modal appears near it,
    // then compute a padding-top to position the modal a bit below that point.
    const el = document.getElementById('freeagents-page');
    if (el) {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        // ignore
      }
    }

    // Wait a short moment for the browser to complete the scroll, then open modal
    setTimeout(() => {
      let padding: number | null = null;
      if (el) {
        const rect = el.getBoundingClientRect();
        // Use rect.top (viewport-relative). Add a small offset so modal is slightly below the grid header.
        padding = Math.max(12, Math.round(rect.top + 12));
      }
      setSignModalPaddingTop(padding);
      setPendingSignPlayer(player);
    }, 180);
  };
  const scrollToCompleteClubSection = useCallback(() => {
    setTimeout(() => {
      completeClubSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  }, []);

  // Ferme le modal de signature ; si le cap était le seul obstacle, ouvre la section repêchés et scrolle vers elle.
  // Si l'alignement principal est déjà complet, guide l'utilisateur vers la finalisation du club.
  const closeSignModal = (capWasBlocking: boolean, lineupComplete: boolean) => {
    setPendingSignPlayer(null);
    setSignModalAnchor(null);
    if (signModalAnchor) {
      previousScrollRef.current = null;
      const focusTarget = signTriggerRef.current?.isConnected
        ? signTriggerRef.current
        : document.getElementById('freeagents-page');
      focusTarget?.focus({ preventScroll: true });
    }
    signTriggerRef.current = null;
    // Reset the overlay padding so future modals are centered again
    setSignModalPaddingTop(null);
    // If lineup complete, go to finalization section (keep existing behaviour)
    if (lineupComplete) {
      scrollToCompleteClubSection();
      previousScrollRef.current = null;
      return;
    }

    // If cap was blocking, open drafted section and scroll to it (keep existing behaviour)
    if (capWasBlocking) {
      setShowDraftedSection(true);
      setTimeout(() => {
        draftedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        previousScrollRef.current = null;
      }, 80);
      return;
    }

    // Default: restore the saved scroll position (if any)
    const prev = previousScrollRef.current;
    if (prev != null && typeof window !== 'undefined') {
      setTimeout(() => {
        window.scrollTo({ top: prev, behavior: 'smooth' });
        previousScrollRef.current = null;
      }, 80);
    } else {
      previousScrollRef.current = null;
    }
  };

  // Close sign modal on Escape key when open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingSignPlayer) {
        // use closeSignModal to ensure we restore scroll position
        closeSignModal(false, false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingSignPlayer]);

  useEffect(() => {
    if (!pendingSignPlayer || !isEmbed || signModalAnchor) return;
    const t = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      signModalRef.current?.focus?.();
    }, 50);
    return () => clearTimeout(t);
  }, [pendingSignPlayer, isEmbed, signModalAnchor]);

  useEffect(() => {
    if (!pendingReleasePlayer || !isEmbed || releaseModalAnchor) return;
    const t = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      releaseModalRef.current?.focus?.();
    }, 50);
    return () => clearTimeout(t);
  }, [pendingReleasePlayer, isEmbed, releaseModalAnchor]);

  // Appelé après le choix de destination dans le modal
  const handleSignConfirmed = async (goingNHL: boolean) => {
    const player = pendingSignPlayer;
    if (!player || !leagueId || !userId || signModalBusy || signBusyRef.current) return;
    // Ref-based guard to prevent double-click race condition
    signBusyRef.current = true;
    // Ne pas fermer immédiatement le modal : afficher le résultat dedans
    setSignResult({ status: null });
    setSignModalBusy(true);

    const pos = (player as FreeAgent).positioncode ?? 'G';
    const isGoalie = !('positioncode' in player) || pos === 'G';
    const salary = (player as any).salary ?? 0;
    const fmt = (n: number) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

    if (goingNHL) {
      // ── Validation alignement principal ────────────────────────
      if (isGoalie) {
        if (rosterCounts.goalies >= maxGoalies) {
          toast.error(`Limite de gardiens atteinte (${rosterCounts.goalies}/${maxGoalies}). Libérez un gardien avant d'en signer un nouveau.`);
          signBusyRef.current = false; setSignModalBusy(false); return;
        }
      } else if (pos === 'D') {
        if (rosterCounts.defense >= maxDef) {
          toast.error(`Limite de défenseurs atteinte (${rosterCounts.defense}/${maxDef}). Libérez un défenseur avant d'en signer un nouveau.`);
          signBusyRef.current = false; setSignModalBusy(false); return;
        }
      } else {
        if (rosterCounts.forwards >= maxForwards) {
          toast.error(`Limite d'attaquants atteinte (${rosterCounts.forwards}/${maxForwards}). Libérez un attaquant avant d'en signer un nouveau.`);
          signBusyRef.current = false; setSignModalBusy(false); return;
        }
      }
      // ── Validation salary cap principal ────────────────────────
      if (leagueSettings?.salary_cap && salary > 0) {
        const newTotal = currentSalaryCap + salary;
        if (newTotal > leagueSettings.max_salary_cap) {
          const available = Math.max(0, leagueSettings.max_salary_cap - currentSalaryCap);
          toast.error(`Masse salariale dépassée. Disponible : ${fmt(available)} — Salaire du joueur : ${fmt(salary)}.`);
          signBusyRef.current = false; setSignModalBusy(false); return;
        }
      }
    } else {
      // ── Validation banc (réserve) ───────────────────────────────
      if (rosterCounts.bench >= maxBench) {
        toast.error(`Limite du banc atteinte (${rosterCounts.bench}/${maxBench}). Libérez une place avant de signer un joueur sur le banc.`);
        signBusyRef.current = false; setSignModalBusy(false); return;
      }
      // ── Validation salary cap réserve ──────────────────────────
      if (leagueSettings?.salary_cap_reserve && salary > 0) {
        const newTotal = currentReserveSalaryCap + salary;
        if (newTotal > leagueSettings.max_salary_cap_reserve) {
          const available = Math.max(0, leagueSettings.max_salary_cap_reserve - currentReserveSalaryCap);
          toast.error(`Masse salariale réserve dépassée. Disponible : ${fmt(available)} — Salaire du joueur : ${fmt(salary)}.`);
          signBusyRef.current = false; setSignModalBusy(false); return;
        }
      }
    }

    const willLineupBeCompleteAfterSign = goingNHL && (
      (isGoalie ? rosterCounts.goalies + 1 : rosterCounts.goalies) >= maxGoalies &&
      (pos === 'D' ? rosterCounts.defense + 1 : rosterCounts.defense) >= maxDef &&
      (!isGoalie && pos !== 'D' ? rosterCounts.forwards + 1 : rosterCounts.forwards) >= maxForwards
    );

    setSigningInProgress(prev => new Set([...prev, player.playerid]));
    try {
      await signPlayerStandard(leagueId, player.playerid, userId, goingNHL, true);
      const name = (player as FreeAgent).skaterfullname ?? (player as FreeAgentGoalie).goaliefullname ?? 'Joueur';
      const dest = goingNHL ? 'équipe principale' : 'banc/réserve';
      toast.success(`${name} signé dans l'${dest} !`);
      // Afficher le message de succès DANS le modal et rappeler la possibilité de libérer un joueur
      setSignResult({ status: 'success', message: `${name} signé dans l'${dest} !` });

      // Flash le bon slot
      const slotKey = !goingNHL ? 'bench' : isGoalie ? 'goalies' : pos === 'D' ? 'defense' : 'forwards';
      setHighlightedPos(prev => new Set([...prev, slotKey]));
      setTimeout(() => setHighlightedPos(prev => { const n = new Set(prev); n.delete(slotKey); return n; }), 1000);

      // Retirer optimistiquement de la liste
      setSkaters(prev => prev.filter(p => p.playerid !== player.playerid));
      setGoalies(prev => prev.filter(g => g.playerid !== player.playerid));

      // Rafraîchir les compteurs + caps silencieusement
      await refreshMetrics();
      if (willLineupBeCompleteAfterSign && !signModalAnchor) {
        scrollToCompleteClubSection();
      }
      // Fermer le modal après un court délai pour que l'utilisateur voie le message
      setTimeout(() => {
        // default: not a cap-blocking close; pass whether lineup is now complete
        closeSignModal(false, willLineupBeCompleteAfterSign);
        setSignResult({ status: null });
      }, 2200);
    } catch (err: any) {
      const msg = err?.message || 'Erreur lors de la signature du joueur.';
      toast.error(msg);
      // Afficher l'erreur DANS le modal pour que l'utilisateur puisse agir (ex: aller aux joueurs repêchés)
      setSignResult({ status: 'error', message: msg });
    } finally {
      signBusyRef.current = false;
      setSignModalBusy(false);
      setSigningInProgress(prev => {
        const next = new Set(prev);
        next.delete(player.playerid);
        return next;
      });
    }
  };

  // Libérer un joueur repêché : appelle releasePlayer(), refresh les métriques et la liste des free agents
  const handleReleaseConfirmed = async () => {
    const player = pendingReleasePlayer;
    if (!player || !leagueId || !userId) return;
    closeReleaseModal(true);

    setReleasingIds(prev => new Set([...prev, player.playerid]));
    try {
      const result = await releasePlayer(player.playerid, userId, leagueId, true);
      if (!result.success) {
        toast.error(result.error?.message || 'Erreur lors de la libération du joueur.');
        return;
      }

      const slotKey = !player.isnhl
        ? 'bench'
        : player.isGoalie ? 'goalies' : player.positioncode === 'D' ? 'defense' : 'forwards';
      setHighlightedPos(prev => new Set([...prev, slotKey]));
      setTimeout(() => setHighlightedPos(prev => { const n = new Set(prev); n.delete(slotKey); return n; }), 1000);

      // Retirer optimistiquement de la liste des repêchés
      setDraftedPlayers(prev => prev.filter(p => p.playerid !== player.playerid));

      toast.success(`${player.name} libéré — masse salariale et alignement mis à jour.`);

      // Rafraîchir tout : caps, compteurs, drafted list, ET les free agents (le joueur redevient dispo)
      const [skatersResult, goaliesResult, selectionStatsResult] = await Promise.all([
        getFreeAgentsStandard(leagueId, userId),
        getFreeAgentsStandardGoalie(leagueId, userId),
        getStandardPlayerSelectionStats(leagueId),
      ]);
      const selectionStatsByPlayerId = buildSelectionStatsMap(selectionStatsResult.data);
      if (skatersResult.data) setSkaters(withSelectionStats(skatersResult.data as FreeAgent[], selectionStatsByPlayerId));
      if (goaliesResult.data) setGoalies(withSelectionStats(goaliesResult.data as FreeAgentGoalie[], selectionStatsByPlayerId));
      await refreshMetrics();
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la libération du joueur.');
    } finally {
      setReleasingIds(prev => {
        const next = new Set(prev);
        next.delete(player.playerid);
        return next;
      });
    }
  };

  const handleComplete = async () => {
    if (!leagueId || !userId) return;
    setCompleting(true);
    try {
      const result = await completeInitialBuild(leagueId, userId);
      if (result.success) {
        clearLeagueRulesAcceptance(leagueId);
        toast.success('Phase de construction terminée ! Retour à l’accueil.');
        window.dispatchEvent(new CustomEvent('dynastypool:buildComplete'));
        navigate('/my-office');
      } else {
        toast.error('Erreur lors de la finalisation de votre équipe.');
      }
    } catch (err: any) {
      toast.error('Erreur lors de la finalisation de votre équipe.');
    } finally {
      setCompleting(false);
      setShowConfirmComplete(false);
    }
  };

  useEffect(() => {
    const nextTab: ActiveTab =
      selectedPositions.length === 1 && selectedPositions[0] === 'G' ? 'goalies' : 'skaters';
    setActiveTab((previousTab) => (previousTab === nextTab ? previousTab : nextTab));
  }, [selectedPositions]);

  // Keep "salaire max" in sync with the remaining salary cap while the checkbox is enabled
  useEffect(() => {
    if (!useRemainingCapAsMax || !leagueSettings?.salary_cap) return;
    const remaining = Math.max(0, leagueSettings.max_salary_cap - currentSalaryCap);
    setMaxSalary(formatSalaryMillionsInput(remaining));
  }, [useRemainingCapAsMax, leagueSettings, currentSalaryCap]);

  // ── Guards ──────────────────────────────────────────────────────
  if (modeLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <span className="spinner-border text-primary" />
      </div>
    );
  }

  if (!isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="standard-only"
        title="Bâtir mon équipe — Standard Flexible uniquement"
        message="La construction libre d'équipe est une fonctionnalité exclusive au mode Standard Flexible. En mode Dynasty/Keeper, utilisez le repêchage et les agents libres pour bâtir votre équipe."
        actionLabel="Retour à mon équipe"
        actionPath="/my-teams"
      />
    );
  }

  // Build phase is complete
  if (!isBuildPhase) {
    return (
      <div className={`card shadow-lg border-0${isEmbed ? ' free-agents-embed build-my-team-embed' : ''}`}>
        <div className="card-body p-5 text-center">
          <button className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center" onClick={() => navigate('/my-teams')}>
            <ArrowLeft size={20} className="me-2" /> Retour à mon équipe
          </button>
          <CheckCircle size={64} className="text-success mb-3" />
          <h4 className="fw-bold mb-2">Phase de construction terminée !</h4>
          <p className="text-muted mb-4">
            Votre équipe initiale est complète. Cette page n'est plus accessible.<br />
            Utilisez les agents libres réguliers pour effectuer des nouvelles signatures.
          </p>
          <button className="btn btn-success" onClick={() => navigate('/my-teams')}>
            Voir mon équipe
          </button>
        </div>
      </div>
    );
  }

  // ── Roster progress ──────────────────────────────────────────────
  const totalCurrent = rosterCounts.forwards + rosterCounts.defense + rosterCounts.goalies;
  const totalMax = maxForwards + maxDef + maxGoalies;

  // Spots restants
  const mainSpotsRemaining = Math.max(0, totalMax - totalCurrent);
  const reserveSpotsRemaining = Math.max(0, maxBench - rosterCounts.bench);

  // Alignement principal complet (attaquants + défenseurs + gardiens au max requis)
  const isLineupComplete =
    rosterCounts.forwards >= maxForwards &&
    rosterCounts.defense >= maxDef &&
    rosterCounts.goalies >= maxGoalies;

  // Moyenne cap disponible par poste restant
  const mainCapRemaining = leagueSettings ? Math.max(0, leagueSettings.max_salary_cap - currentSalaryCap) : 0;
  const reserveCapRemaining = leagueSettings ? Math.max(0, leagueSettings.max_salary_cap_reserve - currentReserveSalaryCap) : 0;
  const mainAvgPerSpot = mainSpotsRemaining > 0 ? mainCapRemaining / mainSpotsRemaining : null;
  const reserveAvgPerSpot = reserveSpotsRemaining > 0 ? reserveCapRemaining / reserveSpotsRemaining : null;

  const formatCompact = (amount: number) => {
    if (amount >= 1_000_000) return `$${(Math.round(amount / 100_000) / 10).toFixed(1)}M`;
    if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
    return `$${Math.round(amount)}`;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const progressItems = [
    { key: 'forwards', label: 'Attaquants', current: rosterCounts.forwards, max: maxForwards, color: 'primary' },
    { key: 'defense', label: 'Défenseurs', current: rosterCounts.defense, max: maxDef, color: 'info' },
    { key: 'goalies', label: 'Gardiens', current: rosterCounts.goalies, max: maxGoalies, color: 'warning' },
    { key: 'bench', label: 'Banc', current: rosterCounts.bench, max: maxBench, color: 'secondary' },
  ];

  const draftedPlayerSections = [
    {
      key: 'forwards',
      label: 'Attaquants',
      badgeClass: 'bg-primary',
      players: draftedPlayers.filter((player) => !player.isGoalie && player.positioncode !== 'D'),
    },
    {
      key: 'defense',
      label: 'Défenseurs',
      badgeClass: 'bg-info',
      players: draftedPlayers.filter((player) => !player.isGoalie && player.positioncode === 'D'),
    },
    {
      key: 'goalies',
      label: 'Gardiens',
      badgeClass: 'bg-warning text-dark',
      players: draftedPlayers.filter((player) => player.isGoalie),
    },
  ].filter((section) => section.players.length > 0);

  const togglePosition = (position: PositionGroup) => {
    setSelectedPositions((previous) =>
      previous.includes(position)
        ? previous.filter((currentValue) => currentValue !== position)
        : [...previous, position]
    );
  };

  // ── Filtres ─────────────────────────────────────────────────────
  const minSalaryValue = parseSalaryMillions(minSalary);
  const maxSalaryValue = parseSalaryMillions(maxSalary);
  const filteredSkaters = skaters
    .filter((p) => {
      if (selectedPositions.length === 0) return true;
      if (selectedPositions.includes('DEF') && p.positioncode === 'D') return true;
      if (selectedPositions.includes('ATT') && ['C', 'L', 'R'].includes(p.positioncode ?? '')) return true;
      return false;
    })
    // Exclude skaters under salary threshold and specific hidden IDs (frontend-only)
    .filter((p) => {
      const salary = Number(p.salary ?? 0);
      const hiddenIds = new Set([
        8484166, 8482100, 8482762, 8483466,
      ]);
      if (hiddenIds.has(Number(p.playerid))) return false;
      return salary >= 800000;
    })
    .filter((p) => p.skaterfullname.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((p) => selectedTeam === 'ALL' || (p.teamabbrevs ?? '').toLowerCase() === selectedTeam.toLowerCase())
    .filter((p) => (minSalaryValue === undefined || p.salary >= minSalaryValue)
      && (maxSalaryValue === undefined || p.salary <= maxSalaryValue));

  const filteredGoalies = goalies
    .filter(() => selectedPositions.length === 0 || selectedPositions.includes('G'))
    // Exclude goalies under salary threshold and specific hidden IDs (frontend-only)
    .filter((g) => {
      const salary = Number(g.salary ?? 0);
      const hiddenIds = new Set([
        8484166, 8482100, 8482762, 8483466,
      ]);
      if (hiddenIds.has(Number(g.playerid))) return false;
      return salary >= 800000;
    })
    .filter((g) => g.goaliefullname.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((g) => selectedTeam === 'ALL' || (g.teamabbrevs ?? '').toLowerCase() === selectedTeam.toLowerCase())
    .filter((g) => (minSalaryValue === undefined || g.salary >= minSalaryValue)
      && (maxSalaryValue === undefined || g.salary <= maxSalaryValue));

  // ── Tri ──────────────────────────────────────────────────────────
  const sortedSkaters = [...filteredSkaters].sort((a, b) => {
    const field = sortConfig.field;
    const getValue = (p: FreeAgent): number | string => {
      if (field === 'ppg') return p.gamesplayed > 0 ? Number((p.points / p.gamesplayed).toFixed(2)) : 0;
      return p[field as keyof FreeAgent] as unknown as number | string;
    };
    const aVal = getValue(a);
    const bVal = getValue(b);
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortConfig.direction === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const sortedGoalies = [...filteredGoalies].sort((a, b) => {
    const aVal = a[goaliesSortConfig.field];
    const bVal = b[goaliesSortConfig.field];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return goaliesSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return goaliesSortConfig.direction === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  // ── Pagination ───────────────────────────────────────────────────
  const activeListLength = activeTab === 'skaters' ? sortedSkaters.length : sortedGoalies.length;
  const totalPages = Math.ceil(activeListLength / ITEMS_PER_PAGE);
  const paginatedSkaters = sortedSkaters.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const paginatedGoalies = sortedGoalies.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleSort = (field: SortField) => {
    setSortConfig((cur) => ({ field, direction: cur.field === field && cur.direction === 'desc' ? 'asc' : 'desc' }));
    setCurrentPage(1);
  };

  const handleGoalieSort = (field: keyof FreeAgentGoalie) => {
    setGoaliesSortConfig((cur) => ({ field, direction: cur.field === field && cur.direction === 'desc' ? 'asc' : 'desc' }));
    setCurrentPage(1);
  };

  // TSLH embed reste sombre (aligné sur /auth?tslh=1) — seul Arsenal/BPM force le clair en embed
  const isDark = (theme === 'dark' && !isEmbed) || isTslhTenant();
  const showTslhEmbedTopScrollbar = isEmbed && isTslhTenant();
  const textMuted = isDark ? '#94a3b8' : '#6c757d';

  return (
    <div id="freeagents-page" tabIndex={-1} className={`card shadow-lg border-0${isEmbed ? ' free-agents-embed build-my-team-embed' : ''}`} style={{ paddingBottom: 80 }}>
      {/* Modal récapitulatif de la ligue (s'affiche à l'entrée en phase build) */}
      {showLeagueSummaryModal && (() => {
        // En embed, .card (backdrop-filter + overflow:hidden) casse le position:fixed → portail vers <body>
        // Styles inline (plutôt que classes + [data-theme]) : évite toute dépendance au timing de l'attribut data-theme
        const summaryCardStyle: React.CSSProperties = {
          border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.28)'}`,
          borderRadius: 12,
          background: isDark ? 'rgba(30, 41, 59, 0.92)' : '#f8fafc',
          padding: '10px 12px',
        };
        const summaryLabelStyle: React.CSSProperties = {
          display: 'block',
          marginBottom: 4,
          fontSize: '0.78rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          color: isDark ? '#cbd5e1' : '#64748b',
        };
        const summaryMutedColor = isDark ? '#94a3b8' : '#6c757d';
        const modal = (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Récapitulatif de la ligue"
          style={{ position: 'fixed', inset: 0, zIndex: 9200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px', overflow: 'hidden' }}
        >
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setShowLeagueSummaryModal(false)} />
          <div
            ref={leagueModalRef}
            tabIndex={-1}
            style={{
              position: 'relative',
              borderRadius: 14,
              padding: 16,
              maxHeight: 'min(88vh, 720px)',
              overflowY: 'auto',
              background: isDark ? '#0f172a' : '#fff',
              color: isDark ? '#e2e8f0' : '#000',
              outline: 'none', // évite le contour pointillé par défaut du navigateur (focus programmatique)
              zIndex: 9300,
              width: 'min(680px, 100%)',
              boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <h3 className="mb-1">Règles de la ligue</h3>
              <p className="mb-0 small" style={{ color: summaryMutedColor }}>
                Tout ce qu’il faut voir avant de choisir tes joueurs.
              </p>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-12 col-sm-6">
                <div style={summaryCardStyle}>
                  <span style={summaryLabelStyle}>Masse salariale principale</span>
                  <strong>{leagueSettings?.salary_cap ? formatCurrency(leagueSettings?.max_salary_cap ?? 0) : 'Aucun plafond'}</strong>
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div style={summaryCardStyle}>
                  <span style={summaryLabelStyle}>Masse salariale réserve</span>
                  <strong>{leagueSettings?.salary_cap_reserve ? formatCurrency(leagueSettings?.max_salary_cap_reserve ?? 0) : 'Aucun plafond'}</strong>
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div style={summaryCardStyle}>
                  <span style={summaryLabelStyle}>Alignement titulaire</span>
                  <div className="small">
                    <div>Attaquants: {leagueSettings?.number_foward ?? '—'}</div>
                    <div>Défenseurs: {leagueSettings?.number_def ?? '—'}</div>
                    <div>Gardiens: {leagueSettings?.number_goalie ?? '—'}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div style={summaryCardStyle}>
                  <span style={summaryLabelStyle}>Banc et limites</span>
                  <div className="small">
                    <div>Spots réserve: {leagueSettings?.number_reserve ?? '—'}</div>
                    <div>Signatures: {leagueSettings?.max_signing ?? 'N/A'}</div>
                    <div>Spots LTIR: {leagueSettings?.max_injury ?? 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ ...summaryCardStyle, marginBottom: '1rem' }}>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <strong>Système de points</strong>
                <span className="small" style={{ color: summaryMutedColor }}>Vue rapide</span>
              </div>
              {leagueSettings ? (
                <div className="row row-cols-1 row-cols-sm-2 g-2 small">
                  <div className="col">But (attaquant): <strong>{leagueSettings.pts_goal_att ?? 'N/A'}</strong></div>
                  <div className="col">Passe (attaquant): <strong>{leagueSettings.pts_ass_att ?? 'N/A'}</strong></div>
                  <div className="col">But (défenseur): <strong>{leagueSettings.pts_goal_def ?? 'N/A'}</strong></div>
                  <div className="col">Passe (défenseur): <strong>{leagueSettings.pts_ass_def ?? 'N/A'}</strong></div>
                  <div className="col">But (gardien): <strong>{leagueSettings.pts_goal_g ?? 'N/A'}</strong></div>
                  <div className="col">Victoire (gardien): <strong>{leagueSettings.pts_win_g ?? 'N/A'}</strong></div>
                  <div className="col">Blanchissage: <strong>{leagueSettings.pts_shu_g ?? 'N/A'}</strong></div>
                  <div className="col">Défaite OT: <strong>{leagueSettings.pts_otlosse_g ?? 'N/A'}</strong></div>
                </div>
              ) : (
                <div className="small">Les paramètres de points ne sont pas disponibles pour cette ligue.</div>
              )}
            </div>

            <div className="d-grid gap-2 d-sm-flex justify-content-sm-end">
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  // dismiss permanently (persists across sessions)
                  const key = `build-my-team-modal-dismissed:${leagueId}:${userId}`;
                  localStorage.setItem(key, '1');
                  setShowLeagueSummaryModal(false);
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
                }}
              >
                J'ai compris — Ne plus afficher
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Close and scroll the main container into view so the selection UI is visible
                  setShowLeagueSummaryModal(false);
                  setTimeout(() => {
                    const el = document.getElementById('freeagents-page');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 80);
                }}
              >
                Continuer vers la sélection
              </button>
            </div>
          </div>
        </div>
        );
        return isEmbed ? createPortal(modal, document.body) : modal;
      })()}
      <div className="card-body p-1">
        {/* ── Modal choix de destination (portal) ── */}
        {pendingSignPlayer && createPortal((() => {
          const p = pendingSignPlayer;
          const isGoalie = !('positioncode' in p);
          const pos = (p as FreeAgent).positioncode ?? 'G';
          const name = (p as FreeAgent).skaterfullname ?? (p as FreeAgentGoalie).goaliefullname ?? 'Joueur';
          const salary = (p as any).salary ?? 0;
          // Après un succès, currentSalaryCap/rosterCounts ont déjà été rafraîchis depuis la DB (le joueur y est inclus) : ne pas re-additionner
          const alreadyApplied = signResult.status === 'success';
          const effectiveSalary = alreadyApplied ? 0 : salary;
          const effectiveSpotIncrement = alreadyApplied ? 0 : 1;
          const mainFull = isGoalie
            ? rosterCounts.goalies >= maxGoalies
            : pos === 'D' ? rosterCounts.defense >= maxDef : rosterCounts.forwards >= maxForwards;
          const benchFull = rosterCounts.bench >= maxBench;
          const mainCapOk = !leagueSettings?.salary_cap || effectiveSalary === 0 || (currentSalaryCap + effectiveSalary) <= leagueSettings.max_salary_cap;
          const reserveCapOk = !leagueSettings?.salary_cap_reserve || effectiveSalary === 0 || (currentReserveSalaryCap + effectiveSalary) <= leagueSettings.max_salary_cap_reserve;
          const formatCompactModal = (n: number) => {
            if (n >= 1_000_000) return `$${(Math.round(n / 100_000) / 10).toFixed(1)}M`;
            if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
            return `$${Math.round(n)}`;
          };
          const projectedMainCapRemaining = leagueSettings
            ? Math.max(0, leagueSettings.max_salary_cap - (currentSalaryCap + effectiveSalary))
            : 0;
          const projectedReserveCapRemaining = leagueSettings
            ? Math.max(0, leagueSettings.max_salary_cap_reserve - (currentReserveSalaryCap + effectiveSalary))
            : 0;
          const mainRosterSpotsRemainingAfterSign = Math.max(
            0,
            (maxForwards + maxDef + maxGoalies) - (rosterCounts.forwards + rosterCounts.defense + rosterCounts.goalies + effectiveSpotIncrement)
          );
          const reserveSpotsRemainingAfterSign = Math.max(0, maxBench - (rosterCounts.bench + effectiveSpotIncrement));
          const formatAverageRemainingPerPlayer = (remainingCap: number, remainingSpots: number) => {
            if (remainingSpots <= 0) return '—';
            return `${formatCompactModal(remainingCap / remainingSpots)}/joueur`;
          };
          const mainCapBlocked = !mainFull && !mainCapOk;
          const reserveCapBlocked = maxBench > 0 && !benchFull && !reserveCapOk;
          const capBlocking = mainCapBlocked || reserveCapBlocked;
          return (
            <div
              className={signModalAnchor ? 'bmt-overlay-card bmt-sign-anchor-layer' : `position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center${isEmbed ? ' bmt-overlay-card' : ''}`}
              style={signModalAnchor ? undefined : {
                zIndex: 1055,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(3px)',
                alignItems: signModalPaddingTop != null ? 'flex-start' : 'center',
                paddingTop: signModalPaddingTop != null ? `${signModalPaddingTop}px` : undefined,
              }}
              onClick={() => closeSignModal(capBlocking, isLineupComplete)}
            >
              {signModalAnchor && <div className="bmt-sign-anchor-backdrop" aria-hidden="true" />}
              <div
                className={`card shadow-lg border-0${isEmbed ? ' bmt-modal-card' : ''}${signModalAnchor ? ' bmt-sign-anchor-card' : ''}`}
                style={{ maxWidth: 440, width: '92%', borderRadius: 16, ...(signModalAnchor ? { maxHeight: signModalAnchor.maxHeight } : {}) }}
                onClick={(e) => e.stopPropagation()}
                ref={signModalRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Signer le joueur"
              >
                <div className="card-body p-4">
                  <h6 className="fw-bold mb-1">Signer le joueur</h6>
                  <p className="text-muted small mb-3">
                    <strong>{name}</strong>{salary > 0 ? ` — ${formatCompactModal(salary)}` : ''}
                  </p>
                  {capBlocking && (
                    <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-start gap-2 small" role="alert">
                      <span style={{ fontSize: '1rem' }}></span>
                      <span>
                        <strong>Plafond salarial dépassé.</strong><br />
                        Allez dans <strong>Joueurs repêchés</strong> pour libérer un joueur et vous faire de l'espace.
                      </span>
                    </div>
                  )}
                  {/* Résumé des places par position */}
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge bmt-status-badge bmt-status-badge--primary">
                      Att : {rosterCounts.forwards}/{maxForwards}
                    </span>
                    <span className="badge bmt-status-badge bmt-status-badge--info">
                      Déf : {rosterCounts.defense}/{maxDef}
                    </span>
                    <span className="badge bmt-status-badge bmt-status-badge--warning">
                      Gard : {rosterCounts.goalies}/{maxGoalies}
                    </span>
                    {maxBench > 0 && (
                      <span className="badge bmt-status-badge bmt-status-badge--secondary">
                      Banc : {rosterCounts.bench}/{maxBench}
                      </span>
                    )}
                  </div>
                  <div className={`row g-3${maxBench === 0 ? ' justify-content-center' : ''}`}>
                    {/* Équipe principale */}
                    <div className={maxBench === 0 ? 'col-12 col-sm-8' : 'col-12 col-sm-6'}>
                      <button
                        className={`btn bmt-sign-destination w-100 d-flex flex-column align-items-center gap-1 py-3 ${
                          mainFull || !mainCapOk || signModalBusy || signResult.status === 'success' ? 'btn-outline-secondary opacity-50' : 'btn-primary'
                        }`}
                        disabled={mainFull || !mainCapOk || signModalBusy || signResult.status === 'success'}
                        onClick={() => handleSignConfirmed(true)}
                      >
                        <span style={{ fontSize: '1.6rem' }}></span>
                        <span className="fw-semibold">{isEmbed ? 'Signer le joueur' : 'Équipe principale'}</span>
                        <span className="small opacity-75">
                          {isGoalie
                            ? `Gardiens : ${rosterCounts.goalies}/${maxGoalies}`
                            : pos === 'D'
                              ? `Défenseurs : ${rosterCounts.defense}/${maxDef}`
                              : `Attaquants : ${rosterCounts.forwards}/${maxForwards}`
                          }
                        </span>
                        {leagueSettings?.salary_cap && (
                          <span className="small opacity-75 text-center">
                            Masse restante si cette signature est faite : {formatCompactModal(projectedMainCapRemaining)} ({formatAverageRemainingPerPlayer(projectedMainCapRemaining, mainRosterSpotsRemainingAfterSign)})
                          </span>
                        )}
                        {mainFull && <span className="badge bg-danger mt-1" style={{ fontSize: '0.65rem' }}>COMPLET</span>}
                        {!mainFull && !mainCapOk && <span className="badge bg-danger mt-1" style={{ fontSize: '0.65rem' }}>CAP DÉPASSÉ</span>}
                      </button>
                    </div>
                    {/* Banc / Réserve — masqué si maxBench = 0 */}
                    {maxBench > 0 && (
                      <div className="col-12 col-sm-6">
                        <button
                          className={`btn bmt-sign-destination w-100 d-flex flex-column align-items-center gap-1 py-3 ${
                            benchFull || !reserveCapOk || signModalBusy || signResult.status === 'success' ? 'btn-outline-secondary opacity-50' : 'btn-outline-primary'
                          }`}
                          disabled={benchFull || !reserveCapOk || signModalBusy || signResult.status === 'success'}
                          onClick={() => handleSignConfirmed(false)}
                        >
                          <span style={{ fontSize: '1.6rem' }}></span>
                          <span className="fw-semibold">Banc / Réserve</span>
                          <span className="small opacity-75">
                            {`Banc : ${rosterCounts.bench}/${maxBench}`}
                          </span>
                          {leagueSettings?.salary_cap_reserve && (
                            <span className="small opacity-75 text-center">
                              Masse restante si cette signature est faite : {formatCompactModal(projectedReserveCapRemaining)} ({formatAverageRemainingPerPlayer(projectedReserveCapRemaining, reserveSpotsRemainingAfterSign)})
                            </span>
                          )}
                          {benchFull && <span className="badge bg-danger mt-1" style={{ fontSize: '0.65rem' }}>COMPLET</span>}
                          {!benchFull && !reserveCapOk && <span className="badge bg-danger mt-1" style={{ fontSize: '0.65rem' }}>CAP DÉPASSÉ</span>}
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Message de résultat (succès / erreur) affiché dans le modal */}
                  {signResult.status && (
                    <div className={`alert ${signResult.status === 'success' ? 'alert-success' : 'alert-danger'} py-2 px-3 mb-3 small`} role="alert">
                      <div className="d-flex align-items-start gap-2">
                        <span style={{ fontSize: '1rem' }}>{signResult.status === 'success' ? '' : ''}</span>
                        <div>
                          <div className="fw-semibold">{signResult.message}</div>
                          <div className="mt-1 small text-muted">Vous pouvez à tout moment aller dans <strong>Joueurs repêchés</strong> pour retirer un joueur de votre alignement pendant la création d'équipe.</div>
                          <div className="mt-2">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => closeSignModal(true, false)}>Voir Joueurs repêchés</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 text-center">
                    {capBlocking && (
                    <button
                      className="btn btn-sm btn-danger w-100 mb-2"
                      onClick={() => closeSignModal(true, isLineupComplete)}
                    >
                      Voir joueurs repêchés pour libérer de l'espace
                    </button>
                  )}
                    <button className="btn btn-sm btn-link text-muted bmt-cancel-link" onClick={() => closeSignModal(capBlocking, isLineupComplete)}>Annuler</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })(), document.body)}

        <div className="px-3 pt-3">
          <EmbedBackButton />
          {isEmbed ? (
            <div className="fae-header">
              <div className="fae-header__eyebrow">{selectedLeague?.name || ''}</div>
              <h1 className="fae-header__title">Bâtir mon équipe</h1>
              <p className="fae-header__notice">Vous pouvez apporter autant de changements que vous le souhaitez à votre formation jusqu'au 7 octobre minuit. Ensuite 2 changements par mois</p>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                  onClick={() => navigate('/league-rules-onboarding', { state: { from: '/build-my-team' } })}
                >
                  <BookOpen size={16} />
                  Voir les règles
                </button>
                {isTslhTenant() && (
                  <a
                    href="https://100pool.ca/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                  >
                    <BookOpen size={16} />
                    Guide des poolers
                  </a>
                )}
              </div>
            </div>
          ) : (
            <>
              <button
                className="btn btn-link text-primary mb-3 p-0 d-flex align-items-center ms-2 mt-2"
                onClick={() => navigate('/my-office')}
              >
                <ArrowLeft size={20} className="me-2" /> Retour
              </button>

              <div className="text-center mb-4">
                <Users size={40} className="text-success mb-2" />
                <h2 className="fw-bold mb-1">Bâtir mon équipe</h2>
                <p className="text-muted small mb-1">Vous pouvez apporter autant de changements que vous le souhaitez à votre formation jusqu'au 7 octobre minuit. Ensuite 2 changements par mois</p>
                <p className="text-muted">
                  {selectedLeague?.name} — Phase de construction initiale
                </p>
              </div>
            </>
          )}
        </div>

        {/* Progress */}
        <style>{`
          @keyframes bmtPop {
            0% { transform: scale(1); }
            35% { transform: scale(1.12); }
            65% { transform: scale(0.97); }
            100% { transform: scale(1); }
          }
          @keyframes bmtGlow {
            0%, 100% { box-shadow: 0 0 0 0px rgba(16,185,129,0); }
            40% { box-shadow: 0 0 0 4px rgba(16,185,129,0.35); }
          }
          .bmt-slot-highlight {
            animation: bmtPop 0.45s ease, bmtGlow 1s ease;
          }
        `}</style>
        <div className={`card mb-4 mx-2${isEmbed ? ' bmt-section-card' : ''}`} style={{ background: isDark ? 'rgba(30,41,59,0.6)' : '#f8f9fa', border: 'none' }}>
          <div
            className="d-flex align-items-center justify-content-between px-4 pt-3 pb-3"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setShowCapInfo(v => !v)}
          >
            <h6 className="fw-bold mb-0">État salarial</h6>
            <div className="d-flex align-items-center gap-2">
              {!showCapInfo && (
                <span className={`badge ${totalCurrent >= totalMax ? 'bg-success' : 'bg-primary'}`} style={{ fontSize: '0.7rem' }}>
                  {totalCurrent}/{totalMax} joueurs
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: textMuted }}>{showCapInfo ? '▲' : '▼'}</span>
            </div>
          </div>
          {showCapInfo && (
          <div className="card-body pt-0 px-4 pb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="small text-muted">Progression de l'alignement</span>
              <span className={`badge ${totalCurrent >= totalMax ? 'bg-success' : 'bg-primary'}`}>
                {totalCurrent} / {totalMax} joueurs principaux
              </span>
            </div>
            <div className="row g-2">
              {progressItems.map((item) => {
                const isFull = item.current >= item.max;
                const pct = Math.min((item.current / item.max) * 100, 100);
                const isHighlighted = highlightedPos.has(item.key);
                return (
                  <div key={item.key} className="col-6 col-md-3">
                    <div
                      className={`rounded-3 p-3 text-center${isHighlighted ? ' bmt-slot-highlight' : ''}`}
                      style={{
                        background: isFull
                          ? (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)')
                          : (isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)'),
                        border: `1px solid ${isFull ? 'rgba(16,185,129,0.4)' : (isDark ? 'rgba(148,163,184,0.15)' : '#e2e8f0')}`,
                        transition: 'background 0.3s, border-color 0.3s',
                      }}
                    >
                      <div className="small mb-1" style={{ color: textMuted }}>
                        {item.label}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2 }}>
                        <span style={{
                          fontSize: '1.6rem', fontWeight: 800, lineHeight: 1,
                          color: isFull ? '#198754' : (isDark ? '#f1f5f9' : '#1e293b'),
                          transition: 'color 0.3s',
                        }}>
                          {item.current}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: textMuted }}>/{item.max}</span>
                      </div>
                      <div className="progress mt-2" style={{ height: 5, borderRadius: 99 }}>
                        <div
                          className={`progress-bar ${isFull ? 'bg-success' : `bg-${item.color}`}`}
                          style={{ width: `${pct}%`, transition: 'width 0.4s ease, background-color 0.3s' }}
                        />
                      </div>
                      {isFull && (
                        <div className="mt-1" style={{ fontSize: '0.7rem', color: '#198754', fontWeight: 600 }}>✓ Complet</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Salary cap display */}
            {leagueSettings?.salary_cap && (
              <div className="row g-3 mt-1">
                <div className="col-12 col-md-6">
                  <div className="rounded-3 p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#e2e8f0'}` }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="small fw-bold">Cap principal</span>
                      <span className={`badge ${currentSalaryCap > leagueSettings.max_salary_cap ? 'bg-danger' : 'bg-success'}`} style={{ fontSize: '0.7rem' }}>
                        {currentSalaryCap > leagueSettings.max_salary_cap ? 'DÉPASSÉ' : 'OK'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-baseline mb-1">
                      <span className="small text-muted">Utilisé</span>
                      <span className="fw-bold" style={{ fontSize: '0.95rem' }}>{formatCurrency(currentSalaryCap)}</span>
                    </div>
                    <div className="progress mb-2" style={{ height: 6 }}>
                      <div
                        className={`progress-bar ${currentSalaryCap > leagueSettings.max_salary_cap ? 'bg-danger' : 'bg-success'}`}
                        style={{ width: `${Math.min((currentSalaryCap / leagueSettings.max_salary_cap) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">Maximum</span>
                      <span className="small">{formatCurrency(leagueSettings.max_salary_cap)}</span>
                    </div>
                    <hr className="my-2" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0' }} />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">Disponible</span>
                      <span className={`fw-bold small ${mainCapRemaining === 0 ? 'text-danger' : 'text-success'}`}>
                        {formatCurrency(mainCapRemaining)}
                      </span>
                    </div>
                    {mainAvgPerSpot !== null && (
                      <div className="mt-2 rounded-2 px-3 py-2 d-flex align-items-center justify-content-between"
                        style={{ background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)' }}>
                        <span className="small" style={{ color: isDark ? '#a5b4fc' : '#6366f1' }}>
                          📐 Moy. / {mainSpotsRemaining} poste{mainSpotsRemaining > 1 ? 's' : ''} restant{mainSpotsRemaining > 1 ? 's' : ''}
                        </span>
                        <span className="fw-bold" style={{ fontSize: '1rem', color: isDark ? '#a5b4fc' : '#6366f1' }}>
                          {formatCompact(mainAvgPerSpot)}
                        </span>
                      </div>
                    )}
                    {mainSpotsRemaining === 0 && (
                      <div className="mt-2 rounded-2 px-3 py-2 text-center small text-success"
                        style={{ background: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
                         Alignement principal complet
                      </div>
                    )}
                  </div>
                </div>

                {leagueSettings.salary_cap_reserve && (
                  <div className="col-12 col-md-6">
                    <div className="rounded-3 p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#e2e8f0'}` }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="small fw-bold">💼 Cap réserve</span>
                        <span className={`badge ${currentReserveSalaryCap > leagueSettings.max_salary_cap_reserve ? 'bg-danger' : 'bg-success'}`} style={{ fontSize: '0.7rem' }}>
                          {currentReserveSalaryCap > leagueSettings.max_salary_cap_reserve ? 'DÉPASSÉ' : 'OK'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-baseline mb-1">
                        <span className="small text-muted">Utilisé</span>
                        <span className="fw-bold" style={{ fontSize: '0.95rem' }}>{formatCurrency(currentReserveSalaryCap)}</span>
                      </div>
                      <div className="progress mb-2" style={{ height: 6 }}>
                        <div
                          className={`progress-bar ${currentReserveSalaryCap > leagueSettings.max_salary_cap_reserve ? 'bg-danger' : 'bg-success'}`}
                          style={{ width: `${Math.min((currentReserveSalaryCap / leagueSettings.max_salary_cap_reserve) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small text-muted">Maximum</span>
                        <span className="small">{formatCurrency(leagueSettings.max_salary_cap_reserve)}</span>
                      </div>
                      <hr className="my-2" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0' }} />
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small text-muted">Disponible</span>
                        <span className={`fw-bold small ${reserveCapRemaining === 0 ? 'text-danger' : 'text-success'}`}>
                          {formatCurrency(reserveCapRemaining)}
                        </span>
                      </div>
                      {reserveAvgPerSpot !== null && (
                        <div className="mt-2 rounded-2 px-3 py-2 d-flex align-items-center justify-content-between"
                          style={{ background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)' }}>
                          <span className="small" style={{ color: isDark ? '#a5b4fc' : '#6366f1' }}>
                            📐 Moy. / {reserveSpotsRemaining} poste{reserveSpotsRemaining > 1 ? 's' : ''} restant{reserveSpotsRemaining > 1 ? 's' : ''}
                          </span>
                          <span className="fw-bold" style={{ fontSize: '1rem', color: isDark ? '#a5b4fc' : '#6366f1' }}>
                            {formatCompact(reserveAvgPerSpot)}
                          </span>
                        </div>
                      )}
                      {reserveSpotsRemaining === 0 && (
                        <div className="mt-2 rounded-2 px-3 py-2 text-center small text-success"
                          style={{ background: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
                          Banc complet
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="alert alert-info d-flex align-items-start gap-2 mt-3 mb-0 py-2">
              <Info size={16} className="mt-1 flex-shrink-0" />
              <span className="small">
                <strong>Phase initiale :</strong> Signez des joueurs.
                Cliquez sur <strong>J'ai terminé mon club</strong> une fois que vous êtes satisfait de votre équipe.
              </span>
            </div>
          </div>
          )}
        </div>

        {/* ── Pointage (système de points de la ligue) ── */}
        <div className={`card mb-4 mx-2${isEmbed ? ' bmt-section-card' : ''}`} style={{ background: isDark ? 'rgba(30,41,59,0.6)' : '#f8f9fa', border: 'none' }}>
          <div
            className="d-flex align-items-center justify-content-between px-4 pt-3 pb-3"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setShowPointSystem(v => !v)}
          >
            <h6 className="fw-bold mb-0">Pointage</h6>
            <div className="d-flex align-items-center gap-2">
              {!showPointSystem && (
                <span className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>
                  Système de points
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: textMuted }}>{showPointSystem ? '▲' : '▼'}</span>
            </div>
          </div>
          {showPointSystem && (
            <div className="card-body pt-0 px-4 pb-4">
              <p className="small mb-3" style={{ color: textMuted }}>
                Voici le système de pointage employé par <strong>{selectedLeague?.name ?? 'la ligue'}</strong> pour cette saison.
                Utilisez-le pour évaluer la valeur des joueurs disponibles.
              </p>
              {leagueSettings ? (
                <div className="row g-3">
                  {/* Attaquants */}
                  <div className="col-12 col-md-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)', border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#e2e8f0'}` }}>
                      <div className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Attaquants</div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>But</span>
                        <span className="fw-bold">{leagueSettings.pts_goal_att ?? 'N/A'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>Passe</span>
                        <span className="fw-bold">{leagueSettings.pts_ass_att ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  {/* Défenseurs */}
                  <div className="col-12 col-md-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)', border: `1px solid ${isDark ? 'rgba(14,165,233,0.3)' : '#e2e8f0'}` }}>
                      <div className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Défenseurs</div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>But</span>
                        <span className="fw-bold">{leagueSettings.pts_goal_def ?? 'N/A'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>Passe</span>
                        <span className="fw-bold">{leagueSettings.pts_ass_def ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  {/* Gardiens */}
                  <div className="col-12 col-md-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.8)', border: `1px solid ${isDark ? 'rgba(234,179,8,0.3)' : '#e2e8f0'}` }}>
                      <div className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Gardiens</div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>But</span>
                        <span className="fw-bold">{leagueSettings.pts_goal_g ?? 'N/A'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>Victoire</span>
                        <span className="fw-bold">{leagueSettings.pts_win_g ?? 'N/A'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>Blanchissage</span>
                        <span className="fw-bold">{leagueSettings.pts_shu_g ?? 'N/A'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="small" style={{ color: textMuted }}>Défaite en prolongation</span>
                        <span className="fw-bold">{leagueSettings.pts_otlosse_g ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning mb-0 py-2 small">
                  Les paramètres de pointage ne sont pas disponibles pour cette ligue.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Section : Joueurs repêchés ── */}
        <div ref={draftedSectionRef} className={`card mb-4 mx-2${isEmbed ? ' bmt-section-card' : ''}`} style={{ background: isDark ? 'rgba(30,41,59,0.6)' : '#f8f9fa', border: 'none' }}>
          <div
            className="d-flex align-items-center justify-content-between px-4 pt-3 pb-3"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setShowDraftedSection(v => !v)}
          >
            <h6 className="fw-bold mb-0">Joueurs repêchés</h6>
            <div className="d-flex align-items-center gap-2">
              <span className={`badge ${draftedPlayers.length > 0 ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                {draftedPlayers.length} joueur{draftedPlayers.length > 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: '0.75rem', color: textMuted }}>{showDraftedSection ? '▲' : '▼'}</span>
            </div>
          </div>
          {showDraftedSection && (
            <div className="card-body pt-0 px-4 pb-4">
              {draftedPlayers.length === 0 ? (
                <div className="alert alert-info d-flex align-items-start gap-2 mb-0 py-2">
                  <Info size={16} className="mt-1 flex-shrink-0" />
                  <span className="small">
                    Aucun joueur signé pour le moment. Utilisez la liste des agents libres ci-dessous pour bâtir votre équipe.
                  </span>
                </div>
              ) : (
                <>
                  <p className="small mb-3" style={{ color: textMuted }}>
                    Voici les joueurs actuellement dans votre organisation. Libérez un joueur pour récupérer sa place et sa masse salariale.
                  </p>
                  <div className="d-flex flex-column gap-3">
                    {draftedPlayerSections.map((section) => (
                      <div key={section.key}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem' }}>{section.label}</h6>
                          <span className={`badge ${section.badgeClass}`} style={{ fontSize: '0.7rem' }}>
                            {section.players.length}
                          </span>
                        </div>
                        <div className="d-flex flex-column gap-1">
                          {section.players.map((p) => {
                            const isReleasing = releasingIds.has(p.playerid);
                            return (
                              <div
                                key={`${p.isGoalie ? 'g' : 's'}-${p.playerid}`}
                                className="d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                                style={{
                                  background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.85)',
                                  border: `1px solid ${isDark ? 'rgba(148,163,184,0.15)' : '#e2e8f0'}`,
                                }}
                              >
                                <span className="fw-semibold flex-grow-1 bmt-player-name" title={p.name} style={{ fontSize: '0.88rem' }}>
                                  {p.name}
                                </span>
                                <span className="fw-bold flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                                  {formatCompact(p.salary)}
                                </span>
                                <button
                                  className="btn btn-sm btn-outline-danger flex-shrink-0 d-flex align-items-center py-0 px-2"
                                  style={{ fontSize: '0.75rem' }}
                                  onClick={(event) => handleRelease(p, event)}
                                  disabled={isReleasing}
                                  title="Libérer le joueur"
                                >
                                  {isReleasing
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: '0.75rem', height: '0.75rem' }} />
                                    : <UserMinus size={12} />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Modal confirmation libération (portal) ── */}
        {pendingReleasePlayer && createPortal((() => {
          const p = pendingReleasePlayer;
          const isReleasing = releasingIds.has(p.playerid);
          return (
            <div
              className={releaseModalAnchor ? 'bmt-overlay-card bmt-sign-anchor-layer' : `position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center${isEmbed ? ' bmt-overlay-card' : ''}`}
              style={releaseModalAnchor ? undefined : { zIndex: 1055, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
              onClick={() => !isReleasing && closeReleaseModal()}
            >
              {releaseModalAnchor && <div className="bmt-sign-anchor-backdrop" aria-hidden="true" />}
              <div
                className={`card shadow-lg border-0${isEmbed ? ' bmt-modal-card bmt-release-modal' : ''}${releaseModalAnchor ? ' bmt-sign-anchor-card' : ''}`}
                style={{ maxWidth: 440, width: '92%', borderRadius: 16, ...(releaseModalAnchor ? { maxHeight: releaseModalAnchor.maxHeight } : {}) }}
                onClick={(e) => e.stopPropagation()}
                ref={releaseModalRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Libérer ce joueur"
              >
                <div className="card-body p-4">
                  <h6 className="fw-bold mb-1">Libérer ce joueur ?</h6>
                  <p className="text-muted small mb-3">
                    <strong>{p.name}</strong>
                    {p.salary > 0 ? ` — ${formatCurrency(p.salary)}` : ''}
                  </p>
                  <div
                    className="rounded-3 p-3 mb-3 small bmt-release-details"
                    style={{
                      background: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(15,23,42,0.5)',
                      border: `1px solid ${isDark ? 'rgba(7, 7, 7, 0.2)' : 'rgba(15,23,42,0.5)'}`,
                    }}
                  >
                    <div>La <strong>place d'alignement</strong> ({p.isnhl ? 'principale' : 'réserve'}) sera libérée.</div>
                    <div>La <strong>masse salariale</strong> sera récupérée ({formatCompact(p.salary)}).</div>
                    <div>Le joueur redeviendra <strong>agent libre</strong> et pourra être signé à nouveau.</div>
                  </div>
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => closeReleaseModal()}
                      disabled={isReleasing}
                    >
                      Annuler
                    </button>
                    <button
                      className="btn btn-danger d-flex align-items-center gap-2"
                      onClick={handleReleaseConfirmed}
                      disabled={isReleasing}
                    >
                      {isReleasing && <span className="spinner-border spinner-border-sm" />}
                      <UserMinus size={16} />
                      Confirmer la libération
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })(), document.body)}

        {/* Complete button */}
        <div ref={completeClubSectionRef} className="d-flex flex-column align-items-center mb-4 gap-2">
          {!isLineupComplete && (
            <div className="alert alert-warning py-2 px-3 mb-0 text-center small" style={{ maxWidth: 480 }}>
              Votre alignement principal n'est pas complet. Vous devez avoir{' '}
              <strong>{maxForwards} attaquant{maxForwards > 1 ? 's' : ''}</strong>,{' '}
              <strong>{maxDef} défenseur{maxDef > 1 ? 's' : ''}</strong> et{' '}
              <strong>{maxGoalies} gardien{maxGoalies > 1 ? 's' : ''}</strong> avant de finaliser.
            </div>
          )}
          {!showConfirmComplete ? (
            <button
              className="btn btn-success btn-lg d-flex align-items-center gap-2 px-4"
              onClick={() => setShowConfirmComplete(true)}
              disabled={!isLineupComplete}
              title={!isLineupComplete ? 'Complétez votre alignement principal avant de finaliser' : undefined}
            >
              <CheckCircle size={20} />
              J'ai terminé mon club
            </button>
          ) : (
            <div className="card border-success p-4 text-center" style={{ maxWidth: 480 }}>
              <h6 className="fw-bold mb-2">Confirmer la finalisation</h6>
              <p className="small text-muted mb-3">
                Une fois la phase de construction terminée, vous ne pourrez plus signer de joueurs gratuitement.
                Les signatures régulières (avec compteur) seront alors activées. Toutefois, jusqu’au début de la saison, le 29 septembre, les signatures d’agents libres seront **illimitées**. Le compteur de signatures commencera à être comptabilisé à partir du début de la saison.
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmComplete(false)}
                  disabled={completing}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleComplete}
                  disabled={completing}
                >
                  {completing && <span className="spinner-border spinner-border-sm" />}
                  <CheckCircle size={16} />
                  Confirmer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Filtres ── */}
        <div className={`card border-0 bg-light mx-2 mb-4${isEmbed ? ' bmt-section-card' : ''}`}>
          <div className="card-body pb-3 px-3">
            <div className="d-flex align-items-center justify-content-between pt-1 pb-2">
              <span className="fw-bold small">Filtres</span>
              {(searchTerm || selectedPositions.length > 0 || selectedTeam !== 'ALL' || minSalary.trim() !== '' || maxSalary.trim() !== '') && (
                <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>actifs</span>
              )}
            </div>
            {/* Barre de recherche */}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
              <div className="input-group flex-grow-1" style={{ minWidth: 200 }}>
                <span className="input-group-text">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un joueur..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>

            {/* Filtres avancés */}
            <div className="mt-2">
                <div className="mb-2">
                  <label className="form-label small fw-semibold mb-1">Positions</label>
                  <div className="d-flex flex-wrap gap-2">
                    {POSITION_CHECKBOXES.map((position) => (
                      <div key={position} className="form-check form-check-inline me-1 mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`build-my-team-pos-${position}`}
                          checked={selectedPositions.includes(position)}
                          onChange={() => { togglePosition(position); setCurrentPage(1); }}
                        />
                        <label className="form-check-label small" htmlFor={`build-my-team-pos-${position}`}>
                          {position}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-semibold mb-1">Équipe NHL</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedTeam}
                      onChange={(e) => { setSelectedTeam(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="ALL">Toutes les équipes</option>
                      {NHL_TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label small fw-semibold mb-1">Salaire min (M$)</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={minSalary}
                      placeholder="Ex. 5.5"
                      min={0}
                      step="0.1"
                      onChange={(e) => { setMinSalary(e.target.value); setCurrentPage(1); }}
                    />
                    {minSalaryValue !== undefined && (
                      <div className="mt-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#6c757d' }}>
                        {formatCurrency(minSalaryValue)}
                      </div>
                    )}
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label small fw-semibold mb-1">Salaire max (M$)</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={maxSalary}
                      placeholder="Illimité, ex. 10.2"
                      min={0}
                      step="0.1"
                      disabled={useRemainingCapAsMax}
                      onChange={(e) => { setMaxSalary(e.target.value); setCurrentPage(1); }}
                    />
                    {maxSalaryValue !== undefined && (
                      <div className="mt-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#6c757d' }}>
                        {formatCurrency(maxSalaryValue)}
                      </div>
                    )}
                    {leagueSettings?.salary_cap && (
                      <div className="form-check mt-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="build-my-team-max-cap-remaining"
                          checked={useRemainingCapAsMax}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUseRemainingCapAsMax(checked);
                            if (checked) {
                              const remaining = Math.max(0, (leagueSettings?.max_salary_cap ?? 0) - currentSalaryCap);
                              setMaxSalary(formatSalaryMillionsInput(remaining));
                            } else {
                              setMaxSalary(UNLIMITED_SALARY_FILTER);
                            }
                            setCurrentPage(1);
                          }}
                        />
                        <label className="form-check-label small" htmlFor="build-my-team-max-cap-remaining">
                          Cap restant ({formatCurrency(Math.max(0, (leagueSettings?.max_salary_cap ?? 0) - currentSalaryCap))})
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-2">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => {
                      setSelectedPositions([]);
                      setSelectedTeam('ALL');
                      setMinSalary(UNLIMITED_SALARY_FILTER);
                      setMaxSalary(UNLIMITED_SALARY_FILTER);
                      setUseRemainingCapAsMax(false);
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              </div>
          </div>
        </div>

        {/* ── Grille (même composant que Agents Libres) ── */}
        {loading ? (
          <div className="text-center py-5">
            <span className="spinner-border text-primary" />
            <p className="text-muted mt-2">Chargement des joueurs disponibles...</p>
          </div>
        ) : (
          <>
            {activeTab === 'skaters' && (
              paginatedSkaters.length === 0 ? (
                <div className="text-center text-muted py-4">Aucun joueur trouvé</div>
              ) : (
                <FreeAgentsTable
                  players={paginatedSkaters}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onSign={handleSign}
                  onViewDetails={handleViewPlayerDetails}
                  language="fr"
                  actionLabel="Signer"
                  signingInProgress={signingInProgress}
                  isEmbed={isEmbed}
                  nameActsAsSign={isEmbed}
                  showTopHorizontalScrollbar={showTslhEmbedTopScrollbar}
                  showSelectionPercentage
                />
              )
            )}

            {activeTab === 'goalies' && (
              paginatedGoalies.length === 0 ? (
                <div className="text-center text-muted py-4">Aucun gardien trouvé</div>
              ) : (
                <FreeAgentsGoalieTable
                  goalies={paginatedGoalies}
                  sortConfig={goaliesSortConfig}
                  onSort={handleGoalieSort}
                  onSign={handleSign}
                  onViewDetails={handleViewPlayerDetails}
                  language="fr"
                  actionLabel="Signer"
                  signingInProgress={signingInProgress}
                  isEmbed={isEmbed}
                  nameActsAsSign={isEmbed}
                  showTopHorizontalScrollbar={showTslhEmbedTopScrollbar}
                  showSelectionPercentage
                />
              )
            )}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)}>«</button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
                    </li>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                      const page = start + i;
                      if (page > totalPages) return null;
                      return (
                        <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)}>»</button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
